import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, forkJoin, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { HTTPCONSTANTS } from '../http/http-constants';

/**
 * Read-only data source for the Editorial Workbench (Pass A3).
 *
 * It reads the PERSISTED editorial artifacts from the Dev2 Plato HttpApi
 * (PlatoAPILambda-dev2) via the Stage A1 read-only GET routes, plus the computed
 * intelligence dossier for summary / timeline. GETs never compute and never call
 * an LLM: a 404 (artifact never generated) is mapped to `null` so the pane can
 * show "not yet generated". The three POSTs are explicit, live/paid actions the
 * user triggers by button only — never on load.
 */
export interface ArtifactRow {
  id: number;
  registration: string;
  artifact_type: string;
  version: number;
  pipeline_run_id: string;
  parent_artifact_id: number | null;
  model: string | null;
  contract_version: string | null;
  passed: boolean | null;
  created_at: string;
  created_by: string;
  payload: any;
}

export interface WorkbenchState {
  dossier: any | null;
  enrichedPack: ArtifactRow | null;
  brief: ArtifactRow | null;
  draft: ArtifactRow | null;
  verification: ArtifactRow | null;
  artifacts: any[];
}

@Injectable({ providedIn: 'root' })
export class EditorialArtifactService {
  constructor(private http: HttpClient) {}

  public normalize(registration: string): string {
    return (registration || '').toUpperCase().replace(/\s+/g, '').trim();
  }

  private base(): string {
    return `${HTTPCONSTANTS.editorialApiProtocol}${HTTPCONSTANTS.editorialApiBaseAddress}`;
  }

  private reg(registration: string): string {
    return encodeURIComponent(this.normalize(registration));
  }

  /** GET the computed intelligence dossier (summary / evidence / media / timeline). */
  public getDossier(registration: string): Observable<any | null> {
    return this.http
      .get<any>(`${this.base()}/admin/editorial/registration/${this.reg(registration)}`)
      .pipe(catchError(() => of(null)));
  }

  /** GET latest persisted artifact of a type; null on 404 (never generated). */
  private getArtifact(registration: string, type: string): Observable<ArtifactRow | null> {
    return this.http
      .get<ArtifactRow>(`${this.base()}/admin/editorial/registration/${this.reg(registration)}/${type}`)
      .pipe(catchError(() => of(null)));
  }

  public getEnrichedPack(reg: string) { return this.getArtifact(reg, 'enriched-pack'); }
  public getEditorialBrief(reg: string) { return this.getArtifact(reg, 'editorial-brief'); }
  public getDraft(reg: string) { return this.getArtifact(reg, 'draft'); }
  public getVerification(reg: string) { return this.getArtifact(reg, 'verification'); }

  /** Version / rounds history (metadata only). Unwraps { registration, artifacts }. */
  public getArtifacts(registration: string): Observable<any[]> {
    return this.http
      .get<any>(`${this.base()}/admin/editorial/registration/${this.reg(registration)}/artifacts`)
      .pipe(
        map((res) => (res && res.artifacts) || []),
        catchError(() => of([])),
      );
  }

  /**
   * READ-ONLY page load: all persisted state in one shot. Zero POSTs, zero LLM,
   * zero cost — only the Stage A1 GETs + the dossier GET.
   */
  public loadAll(registration: string): Observable<WorkbenchState> {
    return forkJoin({
      dossier: this.getDossier(registration),
      enrichedPack: this.getEnrichedPack(registration),
      brief: this.getEditorialBrief(registration),
      draft: this.getDraft(registration),
      verification: this.getVerification(registration),
      artifacts: this.getArtifacts(registration),
    });
  }

  // ---- Explicit, LIVE/PAID generation (button-only; never on load) ---------- #

  /**
   * POST build-dossier — the FIRST pipeline stage for any plate: reads the verified
   * spine (sales) READ-ONLY from prod via editorial_ro, optionally runs media/
   * reference discovery, and persists the dossier tables in dev. Required before
   * editorial-brief for a plate that has no existing dossier. Paid/live.
   */
  public buildDossier(registration: string, discover = true): Observable<any> {
    return this.http
      .post<any>(
        `${this.base()}/admin/editorial/registration/${this.reg(registration)}/build-dossier?discover=${discover}`,
        {},
      )
      .pipe(catchError((e) => of({ error: true, status: e?.status, detail: e?.error?.detail || 'request failed' })));
  }

  /**
   * POST save-edited-draft — persist the editor's finished/polished article (pasted
   * back after external working-up) as a NEW draft artifact version. Append-only:
   * version history is preserved and this becomes the current draft. No LLM/audit.
   */
  public saveEditedDraft(registration: string, markdown: string, headline?: string | null): Observable<any> {
    return this.http
      .post<any>(
        `${this.base()}/admin/editorial/registration/${this.reg(registration)}/save-edited-draft`,
        { markdown, headline: headline || null, editor: 'workbench' },
      )
      .pipe(catchError((e) => of({ error: true, status: e?.status, detail: e?.error?.detail || 'request failed' })));
  }

  /**
   * POST add-media — editor-inserted media (a pasted image/media URL, or a hosted
   * upload URL). The editor asserts rights, so it is stored cleared + attributed and
   * appended to the pack as a new version. Shows in the media candidates grid.
   */
  public addMedia(
    registration: string,
    media: { image_url: string; media_type?: string; caption?: string | null; attribution?: string | null; attribution_url?: string | null },
  ): Observable<any> {
    return this.http
      .post<any>(`${this.base()}/admin/editorial/registration/${this.reg(registration)}/add-media`, {
        image_url: media.image_url,
        media_type: media.media_type || 'image',
        caption: media.caption || null,
        attribution: media.attribution || null,
        attribution_url: media.attribution_url || null,
        added_by: 'workbench',
      })
      .pipe(catchError((e) => of({ error: true, status: e?.status, detail: e?.error?.detail || 'request failed' })));
  }

  /** POST research-enrich + brief (persists enriched_pack + editorial_brief). Paid. */
  public generatePackAndBrief(registration: string): Observable<any> {
    return this.http
      .post<any>(`${this.base()}/admin/editorial/registration/${this.reg(registration)}/editorial-brief`, {})
      .pipe(catchError((e) => of({ error: true, detail: e?.error?.detail || 'request failed' })));
  }

  /** POST generate-draft (writer -> verify -> atomic persist). Paid. */
  public generateDraft(registration: string): Observable<any> {
    return this.http
      .post<any>(`${this.base()}/admin/editorial/registration/${this.reg(registration)}/generate-draft`, {})
      .pipe(catchError((e) => of({ error: true, detail: e?.error?.detail || 'request failed' })));
  }

  /**
   * POST inject-source (Human Injection, Type 2). LIVE: fetches + reconciles the URL
   * into a NEW persisted enriched_pack version. Not an LLM writer call, but it does
   * fetch a page, so it is an explicit action. Errors (e.g. 422 unfetchable) surface
   * as { error, detail } rather than throwing.
   */
  public injectSource(
    registration: string,
    url: string,
    supportsClaim?: string | null,
    editorConfidence?: number | null,
    editorNote?: string | null,
  ): Observable<any> {
    return this.http
      .post<any>(
        `${this.base()}/admin/editorial/registration/${this.reg(registration)}/inject-source`,
        {
          url,
          supports_claim: supportsClaim || null,
          editor_confidence: editorConfidence || null,
          editor_note: editorNote || null,
          injected_by: 'workbench',
        },
      )
      .pipe(catchError((e) => of({ error: true, status: e?.status, detail: e?.error?.detail || 'request failed' })));
  }

  /**
   * POST inject-source-nl (B3 natural-language flow). The editor writes a plain-English
   * instruction and (optionally) a URL; the backend LLM routes it to a claim, then the
   * UNCHANGED deterministic rules decide promotion. Returns a plain-English `message`.
   * The instruction never becomes a fact — it only directs which claim to verify.
   */
  public injectSourceNL(
    registration: string,
    instruction: string,
    url?: string | null,
    editorConfidence?: number | null,
  ): Observable<any> {
    return this.http
      .post<any>(
        `${this.base()}/admin/editorial/registration/${this.reg(registration)}/inject-source-nl`,
        {
          instruction,
          url: url || null,
          editor_confidence: editorConfidence || null,
          injected_by: 'workbench',
        },
      )
      .pipe(catchError((e) => of({ error: true, status: e?.status, detail: e?.error?.detail || 'request failed' })));
  }
}
